import Config from "./Config.es6";

export default class {
    constructor(canvas, state) {
        this.canvas = canvas;
        this.State  = state;
    }

    // Variables
    get bioLimit()      { return 0.05 }
    get ctx()           { return this.canvas.getContext('2d') }
    get bioLogo()       { return this.loadImage(Config.bioLogo) }
    get label()         { return this.loadImage(Config.sticker) }
    get background()    { return this.loadImage(this.State.image) }

    loadImage(src) {
        return new Promise((resolve, reject) => {
            const image         = new Image();
            image.crossOrigin   = 'anonymous'; // To handle cross-origin requests'
            image.onload        = () => resolve(image);
            image.onerror       = reject;
            image.src           = src;
        });
    }

    /**
     * Define if we have a fill-width or only half-with background
     * @param background
     * @returns {boolean}
     */
    isFullDesign(background) {
        // Do not calculate if we use empty background
        if (background.src === this.State.DEFAULT_IMAGE_NAME) return false;
        return background.width === 2215;
    }

    /**
     * Clear the entire canvas and reset transformations
     */
    reset() {
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }

    drawDynamicText(color, text, maxFontSize, rightOffset, bottomOffset, maxWidth, maxHeight) {
        this.ctx.save();
        this.ctx.textBaseline   = 'bottom';
        this.ctx.textAlign      = 'left';
        this.ctx.fillStyle      = color;

        let fontSize = maxFontSize;
        let textWidth;
        let textFits = false;

        while (!textFits && fontSize > 0) {
            this.ctx.font = `bold ${fontSize}px futura-pt-bold, Arial, sans-serif`;
            textWidth = this.ctx.measureText(text).width;
            if (textWidth <= maxHeight && fontSize <= maxWidth) {
                textFits = true;
            } else {
                fontSize--;
            }
        }

        if (textFits) {
            const x = this.canvas.width - rightOffset;
            const y = this.canvas.height - textWidth - 50;
            this.ctx.translate(x, y);
            this.ctx.rotate(-Math.PI / 2);
            this.ctx.fillText(text, -textWidth, 0);
        }
        this.ctx.restore();
    }

    wrapText(text = '', x, y, maxWidth, lineHeight, angle, font = 'futura-pt', color = '#000000', align = 'left') {
        this.ctx.save();

        // Translate to the desired position
        this.ctx.translate(x, y);

        // Rotate the context
        this.ctx.rotate(angle);

        // Set up text properties
        this.ctx.font = `22px ${font}, futura-pt, sans-serif`;
        this.ctx.fillStyle = color;
        this.ctx.textAlign = align;

        let words = text.split(' ');
        let line = '';
        let testLine = '';
        let lineArray = [];
        let currentY = 0;

        for (let n = 0; n < words.length; n++) {
            testLine += `${words[n]} `;
            let metrics = this.ctx.measureText(testLine);
            let testWidth = metrics.width;

            if (testWidth > maxWidth && n > 0) {
                lineArray.push(line);
                line = `${words[n]} `;
                testLine = `${words[n]} `;
                currentY += lineHeight;
            } else {
                line += `${words[n]} `;
            }
        }
        lineArray.push(line);


        // Draw text
        for (let i = 0; i < lineArray.length; i++) {
            if (align === 'center') {
                this.ctx.fillText(lineArray[i], maxWidth / 2, (i + 1) * lineHeight);
            } else if (align === 'right') {
                this.ctx.fillText(lineArray[i], maxWidth, (i + 1) * lineHeight);
            } else {
                this.ctx.fillText(lineArray[i], 0, (i + 1) * lineHeight);
            }
        }

        this.ctx.restore();
    }

    // Main function to draw  icons and text layout
    async drawFixedIconLayout(data = [], startX, startY, redBoxWidth, redBoxHeight) {
        const itemWidth     = 150;
        const itemHeight    = 110;
        const iconSize      = Config.icons.size;

        if (data.length > Config.icons.max) {
            console.log("More than 11 icons provided. Hiding the entire element.");
            return; // Exit the function without drawing anything
        }

        this.ctx.save();
        this.ctx.translate(startX, startY);
        this.ctx.rotate(-Math.PI / 2);

        const itemsPerRow = Math.floor(redBoxWidth / itemWidth);

        for (let i = 0; i < Math.min(data.length, Config.icons.max); i++) {
            const item = data[i];
            const row = Math.floor(i / itemsPerRow);
            const col = i % itemsPerRow;

            const x = col * itemWidth;
            const y = row * itemHeight;

            // Calculate center positions
            const iconCenterX = x + itemWidth / 2;
            const iconTopY = y + 30; // 30px from top of blue box

            // Draw SVG icon
            try {
                const image = await this.loadImage(item.image);
                image.width     = iconSize;
                image.height    = iconSize;
                this.ctx.drawImage(
                    image,
                    iconCenterX - image.width/2,
                    iconTopY - image.height/2,
                    image.width,
                    image.height
                );
            } catch (error) {
                console.error('Error loading or drawing SVG:', error);
                // Draw placeholder if SVG fails to load
                this.ctx.fillStyle = '#cccccc';
                this.ctx.beginPath();
                this.ctx.arc(iconCenterX, iconTopY + iconSize / 2, iconSize / 2, 0, 2 * Math.PI);
                this.ctx.fill();
            }

            // Draw text
            this.ctx.fillStyle = '#000000';
            this.ctx.font = '14px Arial';
            this.ctx.textAlign = 'center';
            this.ctx.textBaseline = 'middle';
            this.ctx.fillText(item.name, iconCenterX, y + itemHeight - 20); // 30px from bottom of blue box
        }
        this.ctx.restore();
    }

    drawSmallAlignedText(color = '#000000', text, fontSize, offset, bottomOffset, opacity, font = 'futura-pt-bold', alignment = 'right') {
        this.ctx.save();
        this.ctx.fillStyle  = `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
        this.ctx.font       = `${fontSize}px ${font}, futura-pt-bold, sans-serif`;

        // Set position based on alignment
        const x = alignment === 'right' ? this.canvas.width - offset : offset;
        const y = this.canvas.height - bottomOffset;

        this.ctx.translate(x, y);
        this.ctx.rotate(-Math.PI / 2);

        // Set text alignment
        this.ctx.textAlign       = alignment;
        this.ctx.textBaseline    = 'bottom';

        // Draw text
        this.ctx.fillText(text, 0, 0);

        this.ctx.restore();
    }

    drawLogo() {
        this.drawSmallAlignedText(
            this.State.primary,
            Config.logo.text,
            Config.logo.fontSize,
            400,
            1794,
            this.State.opacity,
            'Sari-Black'
        );
    }

    drawSideText() {
        this.drawDynamicText(
            this.State.secondary,
            this.State.name,
            197,
            1100,
            0,
            this.canvas.height - 1000,
            this.canvas.height - 100
        );
        return this;
    }

    drawNameOnLogo() {
        this.drawSmallAlignedText(
            this.State.primary,
            this.State.name,
            92,
            345,
            1785,
            this.State.opacity
        );
        return this;
    }

    drawNameOnSticker() {
        this.drawSmallAlignedText(
            '#000000',
            this.State.name,
            30,
            160,
            845,
            1,
            'futura-pt-bold',
            'left'
        );
        return this;
    }

    drawDescriptionOnSticker() {
        this.wrapText(this.State.description, 160, 1152, 1070, 25, -Math.PI / 2);
        return this;
    }

    async drawSticker() {
        const stickerHeight = this.canvas.height * 0.94;
        this.ctx.drawImage(
            await this.label,
            100,
            (this.canvas.height - stickerHeight) / 2,
            700,
            stickerHeight
        );
    }

    async drawBioLogo() {
        this.ctx.drawImage(
            await this.bioLogo,
            1840,
            1780,
            100,
            100
        );
    }

    async drawBackground() {
        // Create, load and set the background image
        this.ctx.fillStyle = this.State.primary;
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        const background = await this.background;
        this.ctx.drawImage(
            background,
            this.isFullDesign(background) ? 0 : this.canvas.width / 2,
            0,
            this.isFullDesign(background) ? this.canvas.width : this.canvas.width / 2,
            this.canvas.height
        );
    }

    async drawIcons(ingredients) {
        await this.drawFixedIconLayout(ingredients, 250, 1155, 1050, 300);
        return this;
    }

    showBioLogo(design) {
        return design?.displayBioLogo && this.State.opacity > this.bioLimit;
    }

    async render(design, ingredients) {
        await this.reset();
        await this.drawBackground();
        await this.drawLogo();
        await this.drawSticker();
        this.drawSideText();
        // Render Name
        this
            .drawNameOnLogo()
            .drawNameOnSticker()
        ;
        // Render Description
        this.drawDescriptionOnSticker();
        // Draw icons
        await this.drawIcons(ingredients);
        // Draw Bio Logo
        if (this.showBioLogo(design)) await this.drawBioLogo();
        return this;
    }
}